import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import {
  LotDataFragment,
  LotTypeEnum,
} from 'src/app/modules/graphql/service/graphql-auto-main-service';
import {
  LotSource,
  LotStatusEnum,
  MotoListLotsGQL,
} from 'src/app/modules/graphql/service/graphql-cars-default-service';
import { LotListDataService } from 'src/app/modules/shared-components/lot-list-data-service/lot-list-data.service';

import {
  DateRange,
  getDaysRange,
  getTypeSort,
  simpleNameSort,
  toLocalIsoString,
} from '../../../../const';

@Component({
  selector: 'app-moto-lots',
  templateUrl: './moto-lots-list.component.html',
  styleUrls: ['./moto-lots-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MotoLotsComponent implements OnInit {
  data: any = {
    total: 0,
    nodes: [],
  };
  LotTypeEnum = LotTypeEnum;
  loading = true;

  page = 1;
  pageSize = 20;

  isYearFiltered = false;
  isEngineVolumeFiltered = false;
  isMileageFiltered = false;
  isActiveFilters = true;
  total = 0;
  companies: any;
  companiesList: any[] = [];
  auctions: any;
  form: FormGroup;
  inclusionFilterFields = {
    frame: 'modelTypeEn',
    score: 'scoresEn',
    auction: 'auctRef',
    color: 'colorEn',
    date: 'date',
    result: 'result',
  };
  lastDay: Date;
  dateRange: DateRange[] = [];
  selectedDays: string[] = [];

  LotSource = LotSource;
  formReady = false;
  lotListData$: Observable<Map<string, LotDataFragment>> = of(new Map());

  constructor(
    private title: Title,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private lotListDataService: LotListDataService,
    private motoListLotsGql: MotoListLotsGQL,
  ) {
    this.initForm();
    this.route.queryParams.subscribe((params) => {
      this.loading = true;
      this.page = +params.page || 1;
      this.pageSize = +params.pageSize || this.pageSize;
      const filter = this.getQueryFilter(params);
      this.form.patchValue(this.getFilterFormState(params));

      const sort = {};
      if (params.sort && params.sortType) {
        sort[params.sort] = {
          direction: params.sortType,
        };
      }

      this.initDate();
      this.motoListLotsGql
        .watch(
          {
            first: this.pageSize,
            offset: (this.page - 1) * this.pageSize,
            filter,
            sort,
          },
          { fetchPolicy: 'network-only' },
        )
        .valueChanges.subscribe(
          (result) => {
            this.data = result.data.lots;
            this.loading = result.loading;
            this.total = result.data.motoLots.total;
            this.lotListData$ = this.lotListDataService.fetchData(
              result.data.lots.nodes,
              LotTypeEnum.Moto,
              LotSource.Aleado,
            );
            this.companies = result.data.motoCompanies.nodes.reduce((carry, company) => {
              carry[company.companyId] = company;
              return carry;
            }, {});
            this.companiesList = [...(result.data.motoCompanies.nodes ?? [])].sort(simpleNameSort);
            this.auctions = this.data.auctRefOptions;
            if (!this.formReady) {
              this.formReady = true;
            }
          },
          (error) => {
            console.log(error);
          },
        );
    });
  }

  initForm(): void {
    this.form = this.fb.group({
      auction: this.fb.control('all'),
      company: this.fb.control(''),
      model: this.fb.control(''),
      bid: this.fb.control(null),
      yearFrom: this.fb.control(null),
      yearTo: this.fb.control(null),
      mileageFrom: this.fb.control(null),
      mileageTo: this.fb.control(null),
      engineVolumeFrom: this.fb.control(null),
      engineVolumeTo: this.fb.control(null),
      inclusionScore: this.fb.control([]),
      inclusionColor: this.fb.control([]),
      datetime: this.fb.control(false),
      hasBids: this.fb.control(false),
      hasNegotiations: this.fb.control(false),
      hasTranslations: this.fb.control(false),
    });
  }

  ngOnInit(): void {
    this.title.setTitle('ECar JP Lots');
    this.isActiveFilters = window.innerWidth >= 780;
  }

  filter(value): void {
    this.router.navigate(['moto'], {
      queryParams: {
        r: Math.floor(Math.random() * 100),
        company: value.company,
        model: value.model,
        auction: value.auction ? (value.auction === 'all' ? undefined : value.auction) : undefined,
        bid: value.bid ? value.bid : undefined,
        yearFrom: value.yearFrom ? value.yearFrom : undefined,
        yearTo: value.yearTo ? value.yearTo : undefined,
        mileageFrom: value.mileageFrom ? value.mileageFrom : undefined,
        mileageTo: value.mileageTo ? value.mileageTo : undefined,
        engineVolumeFrom: value.engineVolumeFrom ? value.engineVolumeFrom : undefined,
        engineVolumeTo: value.engineVolumeTo ? value.engineVolumeTo : undefined,
        score: value.inclusionScore.length > 0 ? value.inclusionScore : undefined,
        color: value.inclusionColor.length > 0 ? value.inclusionColor : undefined,
        date: this.selectedDays.length > 0 ? this.selectedDays : undefined,
        datetime: value.datetime
          ? DateTime.local().setZone('Asia/Tokyo').plus({ minutes: 15 }).toISO()
          : undefined,
        page: null,
        hasBids: value.hasBids ? value.hasBids : undefined,
        hasNegotiations: value.hasNegotiations ? value.hasNegotiations : undefined,
        hasTranslations: value.hasTranslations ? value.hasTranslations : undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  getQueryFilter(params) {
    const inclusionFilter = [];
    Object.keys(this.inclusionFilterFields).forEach((key) => {
      if (params[key] && params[key].length > 0) {
        if (params[key] !== 'any') {
          inclusionFilter.push({
            fieldName: this.inclusionFilterFields[key],
            eq: [].concat(params[key]),
          });
        }
      }
    });

    return {
      companyRef: {
        eq: params.company,
      },
      modelNameRef: {
        eq: params.model,
      },
      year: {
        gte: +params.yearFrom,
        lte: +params.yearTo,
      },
      mileage: {
        gte: +params.mileageFrom,
        lte: +params.mileageTo,
      },
      engineVolume: {
        gte: +params.engineVolumeFrom,
        lte: +params.engineVolumeTo,
      },
      bid: {
        eq: params.bid,
      },
      inclusion:
        inclusionFilter.length > 0
          ? {
              columns: inclusionFilter,
            }
          : undefined,
      datetime: {
        gt: params.datetime,
      },
      bidsCount: params.hasBids === 'true' ? { gte: 1 } : undefined,
      savedLotStatus:
        params.hasNegotiations === 'true' ? { eq: LotStatusEnum.Negotiations } : undefined,
      translationsCount: params.hasTranslations === 'true' ? { gte: 1 } : undefined,
    };
  }

  getFilterFormState(params) {
    this.isYearFiltered = (params.yearFrom || params.yearTo) as boolean;
    this.isMileageFiltered = (params.mileageFrom || params.mileageTo) as boolean;
    this.isEngineVolumeFiltered = (params.engineVolumeFrom || params.engineVolumeTo) as boolean;
    this.selectedDays = params.date ? [].concat(params.date) : [];
    return {
      auction: params.auction ? params.auction : 'all',
      company: params.company ? params.company : null,
      bid: params.bid ? params.bid : null,
      model: params.model ? +params.model : null,
      yearFrom: params.yearFrom || null,
      yearTo: params.yearTo || null,
      mileageFrom: params.mileageFrom || null,
      mileageTo: params.mileageTo || null,
      engineVolumeFrom: params.engineVolumeFrom || null,
      engineVolumeTo: params.engineVolumeTo || null,
      inclusionScore: params.score ? [].concat(params.score) : [],
      inclusionColor: params.color ? [].concat(params.color) : [],
      datetime: params.datetime || false,
      hasBids: params.hasBids || false,
      hasNegotiations: params.hasNegotiations || false,
      hasTranslations: params.hasTranslations || false,
    };
  }

  resetFilter(): boolean {
    this.form.reset(this.getFilterFormState({}));
    this.selectedDays = [];
    this.router.navigate(['moto']);
    return false;
  }

  changePage(page: number): void {
    this.router.navigate(['moto'], {
      queryParams: {
        page,
      },
      queryParamsHandling: 'merge',
    });
  }

  changePageSize(pageSize: number): void {
    this.router.navigate(['moto'], {
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });
  }

  sortEvent(sort: { key: string; value: string }): void {
    this.router.navigate(['moto'], {
      queryParams: {
        sort: sort.key,
        sortType: getTypeSort(sort.value),
      },
      queryParamsHandling: 'merge',
    });
  }

  removeFilter(filter): void {
    if (filter === 'year') {
      this.form.get('yearFrom').setValue(null);
      this.form.get('yearTo').setValue(null);
    }
    if (filter === 'engineVolume') {
      this.form.get('engineVolumeFrom').setValue(null);
      this.form.get('engineVolumeTo').setValue(null);
    }
    if (filter === 'mileage') {
      this.form.get('mileageFrom').setValue(null);
      this.form.get('mileageTo').setValue(null);
    }
    this.filter(this.form.value);
  }

  scrollToTable(): void {
    document
      .querySelector('.table')
      .scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  change(event): void {
    console.log('change event', event);
    if (!this.loading) {
      this.filter(this.form.value);
    }
  }

  selectDateAuction(event, date: DateRange): void {
    const dateJapan = toLocalIsoString(
      this.getDate(date.date.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' })),
    );
    date.checked = event;
    if (event) {
      this.selectedDays.push(dateJapan);
    } else {
      const day = this.selectedDays.indexOf(dateJapan);
      if (day > -1) {
        this.selectedDays.splice(day, 1);
      }
    }
    this.filter(this.form.value);
  }

  initDate(): void {
    this.lastDay = new Date();
    this.lastDay.setDate(this.lastDay.getDate() + 7);
    if (this.dateRange.length === 0) {
      const dates = getDaysRange(new Date(), new Date(this.lastDay));
      dates.map((item) => {
        this.dateRange.push({
          date: item,
          checked: this.selectedDays.indexOf(toLocalIsoString(this.getDate(item))) > -1,
        });
      });
    } else {
      this.dateRange.map((item) => {
        item.checked = this.selectedDays.indexOf(toLocalIsoString(this.getDate(item.date))) > -1;
      });
    }
  }

  getDate(date = null): Date {
    if (date) {
      return new Date(date);
    }

    return new Date();
  }
}
